import {
  Autocomplete,
  Box,
  InputAdornment,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import { t } from 'i18next';
import { ReportsFolderPreviewFilters } from '@views/pages/Reporting/ReportsFolderPreview/types.ts';
import { useReportsFolderPreviewTableFilters } from '@views/pages/Reporting/ReportsFolderPreview/hooks';
import { TOOLBAR_FIELD_GAP, toolbarFieldSx } from '@ui';

interface IReportsFolderPreviewFiltersProps {
  handleFilterChange: (
    newFilters: Partial<ReportsFolderPreviewFilters>,
  ) => void;
  filters: ReportsFolderPreviewFilters;
}

export const ReportsFolderPreviewTableFilters = ({
  filters,
  handleFilterChange,
}: IReportsFolderPreviewFiltersProps) => {
  const {
    handleSearchChange,
    handleSelectFolder,
    folder,
    folderOptions,
    org,
    handleSelectOrg,
    organizationOptions,
    report_type,
    typeOptions,
    handleSelectType,
  } = useReportsFolderPreviewTableFilters({ filters, handleFilterChange });

  return (
    <Box
      sx={{
        width: '100%',
        padding: '1rem',
        display: 'flex',
        gap: TOOLBAR_FIELD_GAP,
        bgcolor: 'white',
        border: theme => `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <OutlinedInput
        id='searchField'
        placeholder={t('reporting.report.filters.search')}
        onChange={debounce(handleSearchChange, 500)}
        sx={toolbarFieldSx}
        startAdornment={
          <InputAdornment sx={{ padding: 0 }} position='start'>
            <SearchIcon />
          </InputAdornment>
        }
      />
      {organizationOptions.length > 1 && (
        <Autocomplete
          sx={toolbarFieldSx}
          disablePortal
          options={organizationOptions}
          getOptionLabel={(option: { id: string; name: string }) => option.name}
          onChange={handleSelectOrg}
          value={org}
          disabled
          renderInput={params => {
            return (
              <TextField
                {...params}
                label={t('reporting.report.filters.organization')}
              />
            );
          }}
        />
      )}

      <Autocomplete
        sx={toolbarFieldSx}
        disablePortal
        options={typeOptions.map(o => ({ id: o.id, name: o.name }))}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option: { id: string; name: string }) => option.name}
        onChange={handleSelectType}
        value={report_type}
        renderInput={params => {
          return (
            <TextField {...params} label={t('reporting.report.filters.type')} />
          );
        }}
      />

      <Autocomplete
        sx={toolbarFieldSx}
        disablePortal
        options={folderOptions}
        getOptionLabel={(option: { id: string; name: string }) => option.name}
        onChange={handleSelectFolder}
        value={folder}
        disabled
        renderInput={params => {
          return (
            <TextField
              {...params}
              label={t('reporting.report.filters.folder')}
            />
          );
        }}
      />
    </Box>
  );
};
